/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import getPhasesFilterList from "api/getPhasesFilterList";
import getParentCategoriesList from "api/getParentCategoriesList";
import getParentCompaniesList from "api/parentCompanies/getParentCompaniesList";
import getPartnersFilterList from "api/partners/getPartnersFilterList";
import Select from "components/common/Select";
import { URL_KEYS } from "constants/dashboard";
import parseErrorMessage from "helpers/parseErrorMessage";
import useEvent from "hooks/useEvent";
import useRouterSearchParams from "hooks/useRouterSearchParams";
import { parentCompaniesListParamsKey } from "queryKeys/parentCompaniesListKey";
import { partnersListParamsKey } from "queryKeys/partnersListKey";
import { parentCategoriesListParamsKey } from "queryKeys/parentCategoriesListKey";
import { phasesListParamsKey } from "queryKeys/phasesListKey";
import STYLES from "./index.css";
import transformOptions from "./transformOptions";
import { ADDITIONAL, DEBOUNCE_TIMEOUT, PER_PAGE } from "./constants";

const INSIGHTFUL_OPTIONS = [{ label: "Insightful", value: "insightful" }];

const contextByFilterName = {
  parentCategory: {
    label: "Parent Category",
    queryKey: parentCategoriesListParamsKey,
    queryFn: getParentCategoriesList,
    urlKey: "parentCategoryId",
  },
  parentCompany: {
    label: "Parent Company",
    queryKey: parentCompaniesListParamsKey,
    queryFn: getParentCompaniesList,
    urlKey: "parentCompanyId",
  },
  partner: {
    label: "Partner",
    queryKey: partnersListParamsKey,
    queryFn: getPartnersFilterList,
    urlKey: "partnerId",
  },
  excludePartner: {
    label: "Exclude Partner",
    queryKey: partnersListParamsKey,
    queryFn: getPartnersFilterList,
    urlKey: "excludePartner",
  },
  excludePhase: {
    label: "Exclude Phase",
    queryKey: phasesListParamsKey,
    queryFn: getPhasesFilterList,
    urlKey: "excludePhase",
  },
  insightful: {
    label: "Review",
    urlKey: "insightful",
    isStatic: true,
  },
};

export default function ProspectsCommonFilter({ filterName, ...props }) {
  const queryClient = useQueryClient();

  const { label, queryKey, queryFn, urlKey, isStatic } = contextByFilterName[filterName];

  const [searchParams, setSearchParams] = useRouterSearchParams();
  const searchValue = searchParams.get(urlKey) ?? null;
  const searchValueName = searchParams.get(`${filterName}Name`) ?? null;

  // eslint-disable-next-line no-nested-ternary
  const initOptions = isStatic
    ? INSIGHTFUL_OPTIONS
    : searchValue && searchValueName
      ? [{ label: searchValueName || searchValue, value: searchValue }]
      : [];

  const [{ isLoading, options }, setOptions] = useState({
    isLoading: false,
    options: initOptions,
  });

  const selected = useMemo(
    () => options?.find((option) => option.value?.toString() === searchValue),
    [options, searchValue],
  );

  const params = new URLSearchParams(searchParams);

  const handleChange = useEvent((option, { action }) => {
    const value = option?.value;
    const optionLabel = option?.label;

    if (value === selected?.value) return;

    if (action === "clear") {
      params.delete(urlKey);
      params.delete(`${filterName}Name`, optionLabel);

      if (filterName === "partner") {
        params.delete("phaseName");
        params.delete("phaseId");
        params.delete("excludePhaseName");
        params.delete("excludePhase");
      }
    } else {
      if (!isStatic) {
        params.set(`${filterName}Name`, option?.label);
      }

      params.set(urlKey, value);
      params.set(`${filterName}Name`, optionLabel);

      if (filterName === "excludePartner") {
        params.delete("partnerName");
        params.delete("partnerId");
        params.delete("phaseName");
        params.delete("phaseId");
        params.delete("excludePhaseName");
        params.delete("excludePhase");
      }

      if (filterName === "partner") {
        params.delete("excludePartnerName");
        params.delete("excludePartner");
        params.delete("phaseName");
        params.delete("phaseId");
        params.delete("excludePhaseName");
        params.delete("excludePhase");
      }

      if (filterName === "excludePhase") {
        params.delete("phaseName");
        params.delete("phaseId");
      }

      if (params.has(URL_KEYS.page)) {
        params.delete(URL_KEYS.page);
      }
    }

    setSearchParams(params);
  });

  const loadOptions = useCallback(
    (search, loadedOptions, { page }) => {
      if (isStatic) return null;

      const offset = page > 0 ? page * PER_PAGE : 0;
      const queryParams = {
        page,
        perPage: PER_PAGE,
        search: search || undefined,
      };

      if (filterName === "phase" || filterName === "excludePhase") {
        const partnerId = searchParams.get("partnerId") || undefined;
        const excludePartner = searchParams.get("excludePartner") || undefined;

        queryParams.partnerId = partnerId ? Number(partnerId) : undefined;
        queryParams.excludePartner = excludePartner ? Number(excludePartner) : undefined;
      }

      setOptions((prevState) => ({ ...prevState, isLoading: true }));

      return queryClient
        .fetchQuery({
          queryKey: queryKey({ queryParams }),
          queryFn,
        })
        .then(({ data: newData }) => {
          const newOptions = newData?.data;
          const total = newData?.total;

          const transformedOptions = transformOptions(newOptions);
          setOptions((prevState) => ({
            isLoading: false,
            options: [...prevState.options, ...transformedOptions],
          }));

          return {
            options: transformedOptions,
            hasMore: total > offset,
            additional: {
              page: page + 1,
            },
          };
        })
        .catch((error) => {
          toast.error(parseErrorMessage(error));

          setOptions((prevState) => ({ ...prevState, isLoading: false }));

          return {
            options: loadedOptions,
            hasMore: false,
            additional: {
              page,
            },
          };
        });
    },
    [filterName, queryClient, queryFn, queryKey, searchParams, isStatic],
  );

  return (
    <Select
      label={label}
      variant="outlined"
      size="lg"
      styles={STYLES}
      placeholder={isLoading ? "Loading..." : "Select"}
      value={selected ? [selected] : []}
      onChange={handleChange}
      {...(isStatic ? { options: INSIGHTFUL_OPTIONS } : { loadOptions, selectComponent: "async" })}
      debounceTimeout={DEBOUNCE_TIMEOUT}
      additional={ADDITIONAL}
      selectComponent="async"
      hideSelectedOptions
      isClearable
      {...props}
    />
  );
}

ProspectsCommonFilter.propTypes = {
  filterName: PropTypes.string.isRequired,
};
